'use client';

import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import Script from 'next/script';

const TurnstileWidget = forwardRef(({ onVerify, onError, onExpire, action = 'generate', theme = 'dark', disabled = false }, ref) => {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const callbacks = useRef({ onVerify, onError, onExpire });
  callbacks.current = { onVerify, onError, onExpire };
  const [loaded, setLoaded] = useState(false);
  const [verified, setVerified] = useState(false);
  const bypass = disabled || process.env.NEXT_PUBLIC_DISABLE_TURNSTILE === 'true';
  const clearToken = () => {
    setVerified(false);
    callbacks.current.onExpire?.();
  };

  useImperativeHandle(ref, () => ({
    reset() {
      clearToken();
      if (bypass) callbacks.current.onVerify?.('local-bypass');
      else if (widgetRef.current !== null) window.turnstile?.reset(widgetRef.current);
    },
    remove() {
      clearToken();
      if (widgetRef.current !== null) window.turnstile?.remove(widgetRef.current);
      widgetRef.current = null;
    },
  }), [bypass]);

  // next/script can reuse an in-flight script across several modal instances.
  // Observe the actual API as well as onLoad/onReady; a cache entry is not readiness.
  useEffect(() => {
    if (bypass || loaded) return;
    const started = Date.now();
    const check = () => {
      if (window.turnstile?.render) { setLoaded(true); return true; }
      if (Date.now() - started >= 60000) { callbacks.current.onError?.(); return true; }
      return false;
    };
    if (check()) return;
    const timer = setInterval(() => { if (check()) clearInterval(timer); }, 100);
    return () => clearInterval(timer);
  }, [bypass, loaded]);

  useEffect(() => {
    if (bypass) {
      callbacks.current.onVerify?.('local-bypass');
      return;
    }
    if (!loaded || !containerRef.current || !window.turnstile) return;
    const id = window.turnstile.render(containerRef.current, {
      sitekey: process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY || '0x4AAAAAABCJfotJVlXxjnKC',
      theme, action, appearance: 'always', retry: 'auto', 'retry-interval': 8000,
      'refresh-expired': 'auto', 'refresh-timeout': 'auto',
      callback(token) { setVerified(true); callbacks.current.onVerify?.(token); },
      'error-callback'() { clearToken(); callbacks.current.onError?.(); },
      'expired-callback': clearToken,
      'timeout-callback': clearToken,
    });
    widgetRef.current = id;
    return () => {
      window.turnstile?.remove(id);
      widgetRef.current = null;
    };
  }, [loaded, bypass, theme, action]);

  if (bypass) return null;
  return <>
    <Script id="botgo-turnstile" src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
      strategy="afterInteractive" onLoad={() => { if (window.turnstile) setLoaded(true); }} onReady={() => { if (window.turnstile) setLoaded(true); }} onError={() => callbacks.current.onError?.()} />
    <div className="my-4 flex min-h-[65px] w-full justify-center" data-verified={verified ? 'true' : 'false'}>
      <div ref={containerRef} />
    </div>
  </>;
});
TurnstileWidget.displayName = 'TurnstileWidget';
export default TurnstileWidget;
